import { makeAutoObservable, runInAction } from "mobx";
import RootStore from "stores/RootStore";
import ServiceStore from "stores/ServiceStore";

interface BatchTicket {
  id?: number;
  ticketNumber: string;
  jobId: number | null;
  customerId: number | null;
  mixDesignId: number | null;

  // Order Details
  orderedBy: string;
  orderedDate: Date;
  requestedDeliveryDate: Date;
  requestedDeliveryTime: string;

  // Quantity
  yardsOrdered: number;
  yardsRemaining: number;
  loadSize: number; // yards per truck
  estimatedLoads: number;

  // Location
  deliveryAddress: string;
  deliveryCity: string;
  deliveryState: string;
  deliveryZip: string;
  deliveryNotes: string;

  // Mix Specifications
  slumpRequirement: number;
  airContentRequirement: number;
  strengthRequirement: string; // e.g., "4000 PSI"
  additives: string[];
  specialInstructions: string;

  // Pricing
  pricePerYard: number;
  deliveryCharge: number;
  additionalCharges: { description: string; amount: number }[];
  estimatedTotal: number;

  // Status
  status: "draft" | "scheduled" | "in_production" | "completed" | "cancelled";
  priority: "normal" | "high" | "urgent";

  // Weather
  weatherConcerns: boolean;
  weatherNotes: string;
}

class BatchTicketGenerationWorkflowStore {
  rootStore: RootStore;

  // Observables
  loading = false;
  error: string | null = null;
  success: string | null = null;
  activeStep = 0;
  ticketData: BatchTicket;

  constructor(rootStore: RootStore) {
    this.ticketData = this.getDefaultTicket();
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false });
  }

  getDefaultTicket = (): BatchTicket => ({
    ticketNumber: this.generateTicketNumber(),
    jobId: null,
    customerId: null,
    mixDesignId: null,

    orderedBy: "",
    orderedDate: new Date(),
    requestedDeliveryDate: new Date(),
    requestedDeliveryTime: "08:00",

    yardsOrdered: 0,
    yardsRemaining: 0,
    loadSize: 10, // Default 10 yards per truck
    estimatedLoads: 0,

    deliveryAddress: "",
    deliveryCity: "",
    deliveryState: "",
    deliveryZip: "",
    deliveryNotes: "",

    slumpRequirement: 4, // Default 4 inches
    airContentRequirement: 6, // Default 6%
    strengthRequirement: "4000 PSI",
    additives: [],
    specialInstructions: "",

    pricePerYard: 0,
    deliveryCharge: 0,
    additionalCharges: [],
    estimatedTotal: 0,

    status: "draft",
    priority: "normal",

    weatherConcerns: false,
    weatherNotes: "",
  });

  generateTicketNumber = (): string => {
    const now = new Date();
    const year = now.getFullYear().toString().slice(-2);
    const month = (now.getMonth() + 1).toString().padStart(2, "0");
    const day = now.getDate().toString().padStart(2, "0");
    const random = Math.floor(Math.random() * 1000).toString().padStart(3, "0");
    return `BT${year}${month}${day}-${random}`;
  };

  // Actions
  setLoading = (loading: boolean) => {
    this.loading = loading;
  };

  setError = (error: string | null) => {
    this.error = error;
  };

  setSuccess = (success: string | null) => {
    this.success = success;
  };

  setActiveStep = (step: number) => {
    this.activeStep = step;
  };

  nextStep = () => {
    if (this.validateCurrentStep()) {
      this.activeStep++;
    }
  };

  prevStep = () => {
    this.activeStep--;
  };

  updateTicketData = <K extends keyof BatchTicket>(
    field: K,
    value: BatchTicket[K]
  ) => {
    this.ticketData[field] = value;

    // Auto-calculate estimated loads when yards or load size changes
    if (field === "yardsOrdered" || field === "loadSize") {
      this.ticketData.estimatedLoads = Math.ceil(
        this.ticketData.yardsOrdered / this.ticketData.loadSize
      );
      this.ticketData.yardsRemaining = this.ticketData.yardsOrdered;
    }

    // Auto-calculate total when pricing changes
    if (field === "pricePerYard" || field === "yardsOrdered" || field === "deliveryCharge") {
      this.calculateTotal();
    }
  };

  addAdditive = (additive: string) => {
    if (!this.ticketData.additives.includes(additive)) {
      this.ticketData.additives = [...this.ticketData.additives, additive];
    }
  };

  removeAdditive = (additive: string) => {
    this.ticketData.additives = this.ticketData.additives.filter(a => a !== additive);
  };

  addAdditionalCharge = (description: string, amount: number) => {
    this.ticketData.additionalCharges = [
      ...this.ticketData.additionalCharges,
      { description, amount }
    ];
    this.calculateTotal();
  };

  removeAdditionalCharge = (index: number) => {
    this.ticketData.additionalCharges = this.ticketData.additionalCharges.filter(
      (_, i) => i !== index
    );
    this.calculateTotal();
  };

  calculateTotal = () => {
    const materialCost = this.ticketData.yardsOrdered * this.ticketData.pricePerYard;
    const additionalTotal = this.ticketData.additionalCharges.reduce(
      (sum, charge) => sum + charge.amount,
      0
    );
    this.ticketData.estimatedTotal = materialCost + this.ticketData.deliveryCharge + additionalTotal;
  };

  // Load job address for delivery location
  loadJobAddress = (jobId: number) => {
    const job = this.jobs.find((j: any) => j.id === jobId);
    if (job) {
      this.ticketData.deliveryAddress = (job.address as string) || "";
      this.ticketData.deliveryCity = (job.city as string) || "";
      this.ticketData.deliveryState = (job.state as string) || "";
      this.ticketData.deliveryZip = (job.zip as string) || "";
    }
  };

  // Validation
  validateCurrentStep = (): boolean => {
    this.setError(null);

    switch (this.activeStep) {
      case 0: // Customer & Job Info
        if (!this.ticketData.customerId) {
          this.setError("Please select a customer");
          return false;
        }
        if (!this.ticketData.jobId) {
          this.setError("Please select a job");
          return false;
        }
        if (!this.ticketData.orderedBy) {
          this.setError("Please specify who ordered this batch");
          return false;
        }
        break;

      case 1: // Mix Design & Quantity
        if (!this.ticketData.mixDesignId) {
          this.setError("Please select a mix design");
          return false;
        }
        if (this.ticketData.yardsOrdered <= 0) {
          this.setError("Yards ordered must be greater than zero");
          return false;
        }
        if (this.ticketData.loadSize <= 0 || this.ticketData.loadSize > 12) {
          this.setError("Load size must be between 1 and 12 yards");
          return false;
        }
        break;

      case 2: // Delivery Details
        if (!this.ticketData.deliveryAddress) {
          this.setError("Please provide a delivery address");
          return false;
        }
        if (!this.ticketData.requestedDeliveryDate) {
          this.setError("Please specify a delivery date");
          return false;
        }
        break;

      case 3: // Mix Specifications
        if (this.ticketData.slumpRequirement < 1 || this.ticketData.slumpRequirement > 10) {
          this.setError("Slump requirement must be between 1 and 10 inches");
          return false;
        }
        if (this.ticketData.airContentRequirement < 0 || this.ticketData.airContentRequirement > 10) {
          this.setError("Air content requirement must be between 0 and 10%");
          return false;
        }
        break;

      case 4: // Pricing
        if (this.ticketData.pricePerYard <= 0) {
          this.setError("Price per yard must be greater than zero");
          return false;
        }
        break;
    }

    return true;
  };

  // Async actions
  loadInitialData = async () => {
    this.setLoading(true);
    this.setError(null);

    try {
      const jobStore = this.rootStore.jobStore as ServiceStore;
      const customersStore = this.rootStore.customersStore as ServiceStore;
      const mixDesignsStore = this.rootStore.mixDesignsStore as ServiceStore;

      await Promise.all([
        jobStore?.find({ query: { archived: false } }),
        customersStore?.find(),
        mixDesignsStore?.find(),
      ]);
    } catch (err: any) {
      runInAction(() => {
        this.setError("Failed to load initial data");
      });
    } finally {
      runInAction(() => {
        this.setLoading(false);
      });
    }
  };

  submitTicket = async () => {
    if (!this.validateCurrentStep()) {
      return;
    }

    this.setLoading(true);
    this.setError(null);

    try {
      // TODO: Create batch ticket when backend service is available
      // This would create a batch_tickets record

      runInAction(() => {
        this.setSuccess("Batch ticket created successfully!");
        this.activeStep = 5; // Success step
      });

    } catch (err: any) {
      runInAction(() => {
        this.setError(err.message || "Failed to create batch ticket");
      });
    } finally {
      runInAction(() => {
        this.setLoading(false);
      });
    }
  };

  reset = () => {
    this.loading = false;
    this.error = null;
    this.success = null;
    this.activeStep = 0;
    this.ticketData = this.getDefaultTicket();
  };

  // Computed values
  get jobs() {
    const jobStore = this.rootStore.jobStore as ServiceStore;
    return jobStore?.entities || [];
  }

  get customers() {
    const customersStore = this.rootStore.customersStore as ServiceStore;
    return customersStore?.entities || [];
  }

  get mixDesigns() {
    const mixDesignsStore = this.rootStore.mixDesignsStore as ServiceStore;
    return mixDesignsStore?.entities || [];
  }

  get selectedJob() {
    if (!this.ticketData.jobId) return null;
    return this.jobs.find((j: any) => j.id === this.ticketData.jobId);
  }

  get selectedCustomer() {
    if (!this.ticketData.customerId) return null;
    return this.customers.find((c: any) => c.id === this.ticketData.customerId);
  }

  get selectedMixDesign() {
    if (!this.ticketData.mixDesignId) return null;
    return this.mixDesigns.find((md: any) => md.id === this.ticketData.mixDesignId);
  }

  get deliveryDateFormatted() {
    return this.ticketData.requestedDeliveryDate.toLocaleDateString();
  }

  get estimatedDuration() {
    // Estimate 30 minutes per load
    return this.ticketData.estimatedLoads * 30;
  }

  get priorityColor() {
    const colors: Record<BatchTicket["priority"], string> = {
      normal: "#2196f3",
      high: "#ff9800",
      urgent: "#f44336",
    };
    return colors[this.ticketData.priority];
  }

  get statusColor() {
    const colors: Record<BatchTicket["status"], string> = {
      draft: "#9e9e9e",
      scheduled: "#2196f3",
      in_production: "#ff9800",
      completed: "#4caf50",
      cancelled: "#f44336",
    };
    return colors[this.ticketData.status];
  }
}

export default BatchTicketGenerationWorkflowStore;
